import * as React from "react";
import { Link } from "react-router-dom";
import { CalendarCheck2, Plus } from "lucide-react";

import { listIntegrations } from "@/api/client";
import { IntegrationsView } from "@/api/types";
import { cn } from "@/lib/utils";
import { availableProviders, calendarProvider, connections } from "@/lib/calendarProviders";

/**
 * "Calendars" card in the home rail: an at-a-glance view of the user's connected
 * calendars, so the first thing they see on landing is whether their real
 * schedule is wired in. Read-only summary — connecting / importing /
 * disconnecting all live on the profile page ({@link CalendarConnectionsPanel});
 * "Manage" deep-links there.
 *
 * <p>Always renders (unlike the profile panel): even with no Google OAuth
 * configured or nothing connected, we show the state rather than vanishing —
 * the connection status <em>is</em> the information the user came to see.</p>
 */
export function CalendarsCard() {
  const [integrations, setIntegrations] = React.useState<IntegrationsView | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    listIntegrations()
      .then((iv) => {
        if (!cancelled) setIntegrations(iv);
      })
      .catch(() => {
        /* rail widget — swallow; the profile page surfaces errors */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The rail widget shows the primary, which the API returns first.
  const held = connections(integrations ?? null)[0] ?? null;
  const available = availableProviders(integrations ?? null).length > 0;

  return (
    <section className="comic-shadow relative overflow-hidden rounded-3xl border border-border border-l-4 border-l-brand-blue bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-brand-blue/10">
          <CalendarCheck2 className="icon-breathe h-4 w-4 text-brand-blue" />
        </span>
        <h2 className="font-display text-sm font-bold tracking-tight text-foreground">Calendars</h2>
      </div>

      {integrations === null ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : !available ? (
        <p className="rounded-2xl bg-muted/40 px-3 py-3 text-sm leading-relaxed text-muted-foreground">
          Calendar sync isn't enabled on this deployment yet. Your agent still
          schedules, it just can't see an external calendar.
        </p>
      ) : held === null ? (
        <div className="space-y-2.5">
          <p className="text-sm leading-relaxed text-muted-foreground">
            No calendar connected. Your agent can't see your real schedule yet.
          </p>
          <Link
            to="/profile"
            className="flex items-center justify-center gap-1.5 rounded-xl border border-brand-blue/50 px-3 py-2 font-display text-xs font-semibold text-brand-blue transition hover:bg-brand-blue/10"
          >
            <Plus className="h-3.5 w-3.5" /> Connect a calendar
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-background shadow-sm">
              {held.provider === "MICROSOFT" ? (
                <MicrosoftMark className="h-4 w-4" />
              ) : (
                <GoogleG className="h-4 w-4" />
              )}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold leading-tight">
                {calendarProvider(held.provider)?.label ?? "Calendar"}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {held.externalEmail ?? "connected account"}
              </div>
              <span
                className={cn(
                  "mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide",
                  held.status === "ACTIVE"
                    ? "bg-brand-teal/15 text-foreground"
                    : "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300",
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    held.status === "ACTIVE" ? "bg-brand-teal" : "bg-amber-500",
                  )}
                />
                {held.status === "ACTIVE" ? "Connected" : "Reconnect needed"}
              </span>
            </div>
          </div>
          <Link
            to="/profile"
            className="block rounded-lg py-1 text-center font-display text-xs font-semibold uppercase tracking-wide text-brand-blue transition hover:bg-brand-blue/10"
          >
            Manage
          </Link>
        </div>
      )}
    </section>
  );
}

/** The Microsoft four-square mark, for branding an Outlook connection. */
function MicrosoftMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 23 23" className={className} aria-hidden>
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  );
}

/** The Google "G" mark (official four-colour), for branding the connection. */
function GoogleG({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
    </svg>
  );
}
